import React, {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {NameProduct} from '../../ui';
import {AddToFavoritesButton} from './AddToFavoritesButton';
import {NumberInputUI} from '../../ui/components/NumberInputUI';
import {AvailabilityUI} from '../../common/ui/AvailabilityUI';
import {TagListUI} from '../../common/ui/TagListUI';

import {useProductSelection} from '../application/useProductSelection';
import {generateItemTags} from '../../common/CatalogService';
import {Modal} from "react-bootstrap";
import {DealSelectorBis} from "./DealSelectorBis";
import ProductPurchase from "./ProductPurchase";
import {AddToCartButton} from "./AddToCartButton";
import {PendingProductButton} from "./PendingProductButton";
import {StockBypassNotice} from "./StockBypassNotice";
import {PriceWithFallback} from "../../common/ui/PriceWithFallback";

/**
 * @param {boolean} value
 * @param {Function} setHover
 * @returns {Function}
 */
const createHoverHandler = (value, setHover) => () => setHover(value);

/**
 * @param {Function} navigate
 * @param {string} productCode
 * @returns {Function}
 */
const createNavigateHandler = (navigate, productCode) =>
    () => navigate(`/articulos/${encodeURIComponent(productCode)}`);

/**
 * @param {Object} props
 * @param {Object} props.details
 * @returns {JSX.Element}
 */
export const ProductFlatPurchaseCardUI = ({details}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [modal, setModal] = useState(false);

    const navigate = useNavigate();
    const {
        productSelection,
        dealSelection,
        setDealSelection,
        setQuantity
    } = useProductSelection(details, 1);

    const tags = useMemo(() => generateItemTags(details), [details]);

    const handleMouseEnter = useCallback(createHoverHandler(true, setIsHovered), []);
    const handleMouseLeave = useCallback(createHoverHandler(false, setIsHovered), []);
    const handleNavigate = useCallback(createNavigateHandler(navigate, details.code), [navigate, details.code]);

    const hasStock = useMemo(() =>
            (details.availableUnits > 0 || details.onDemand),
        [details.availableUnits, details.onDemand]
    );

    const dealData = React.useMemo(() => ({
        offers: details.offers,
        lots: details.lots,
        promos: details.promos,
        hasDealsToChoose: details.hasDealsToChoose
    }), [details]);

    const recap = React.useMemo(() => {
        if (!productSelection?.price || !productSelection?.discount) {
            return;
        }
        return ({
            price: {
                value: productSelection.price.basePrice * productSelection.quantity,
                bestPrice: productSelection.price.sellingPrice * productSelection.quantity
            },
            discount: {
                value: productSelection.discount.value,
                percentage: productSelection.discount.percentage,
                line: productSelection.discount.text
            }
        })
    }, [productSelection]);

    return (
        <div
            className="rounded-2 p-1 shadow-sm"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="row justify-content-start align-items-center">
                <div className="col-12 col-md-2 col-xl-auto">
                    <div className="d-flex justify-content-center align-items-center">
                        <div className="position-absolute">
                            <AddToFavoritesButton
                                data={{
                                    productCode: details.code,
                                    show: isHovered,
                                    isFavorite: details.isFavorite,
                                }}
                            />
                        </div>
                        <img
                            src={details.imageUrl}
                            onClick={handleNavigate}
                            style={{height: '65px', width: '65px', objectFit: 'contain'}}
                            className="rounded"
                            alt="Imagen del producto"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-8 col-xl">
                    <div className="row gap-2 gap-lg-0">
                        <div className="col-12 col-lg-8 col-xl-8">
                            <div
                                className="d-flex flex-row justify-content-between"
                                style={{cursor: 'pointer'}}
                                onClick={handleNavigate}
                            >
                                <div className="d-flex flex-column">
                                    <TagListUI data={{tags}}/>
                                    <NameProduct>
                                        {details.name}
                                        <small className="text-muted ms-1">Ref: {details.code}</small>
                                    </NameProduct>
                                    <AvailabilityUI data={{availability: productSelection.availability}}/>
                                </div>
                                <PriceWithFallback data={{ sellingPrice: details.price?.bestPrice, basePrice: details.price?.value }} />
                            </div>
                        </div>

                        <div className="col-12 col-lg-4 col-xl-4">
                            <div className="d-flex flex-row gap-2 justify-content-end pe-2">
                                {(details.isPending || !hasStock) && !details.stockBypass && (
                                    <PendingProductButton
                                        productCode={details.code}
                                        defaultValue={details.isPending}
                                        quantity={productSelection.quantity}
                                    />
                                )}
                                <NumberInputUI
                                    data={{value: productSelection.quantity}}
                                    actions={{setValue: setQuantity}}
                                />
                                <div>
                                    <Modal show={modal} onHide={() => setModal(false)} centered size="lg">
                                        <Modal.Header closeButton>
                                            <Modal.Title>{details.name}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <DealSelectorBis dealData={dealData} selection={dealSelection}
                                                             onSelectionChange={setDealSelection}/>
                                            {/* Igual que en la ficha: sin aviso de bajo pedido si hay stock */}
                                            {!(details.availableUnits > 0) &&
                                                <StockBypassNotice product={details} className="mt-3 mb-0" />
                                            }
                                        </Modal.Body>
                                        <Modal.Footer className="d-block">
                                            <ProductPurchase recap={recap}>
                                                <ProductPurchase.PurchaseControls>
                                                    <div
                                                        className="d-flex gap-4 align-items-center justify-content-center justify-content-xl-end">
                                                        <NumberInputUI data={{value: productSelection.quantity}} actions={{setValue: setQuantity}}/>
                                                        <AddToCartButton productSelection={productSelection} hasStock={hasStock} product={details} afterAddToCart={() => setModal(false)}/>
                                                    </div>
                                                </ProductPurchase.PurchaseControls>
                                            </ProductPurchase>
                                        </Modal.Footer>
                                    </Modal>
                                    <div style={{minWidth: '135px'}}>
                                        <AddToCartButton productSelection={productSelection} hasStock={hasStock} product={details} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
